import { Component, EventEmitter, Injector, Output, Renderer2, ViewChild } from '@angular/core';
import { MatFormFieldControlBase } from '@fi-shared/widget/mat-form-field-control-base';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ValidationErrors } from '@angular/forms';
import { FiContenteditableComponent } from '@fi-shared/fi-contenteditable/fi-contenteditable.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiContenteditableService } from '@fi-shared/fi-contenteditable/fi-contenteditable.service';
import { ITaskEditName } from '@fi-shared/fi-task-shared/task-edit/task-edit';

const maxNameLength = 50;

@Component({
    selector: 'fi-task-edit-task-name-input',
    templateUrl: './task-edit-task-name-input.component.html',
    styleUrls: ['./task-edit-task-name-input.component.scss'],
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: TaskEditTaskNameInputComponent,
        },
    ],
    standalone: false,
})
export class TaskEditTaskNameInputComponent extends MatFormFieldControlBase<ITaskEditName> {
    @ViewChild(FiContenteditableComponent, { static: true }) contenteditableComponent: FiContenteditableComponent;
    @Output() filesPasted = new EventEmitter<File[]>();

    focused = false;
    placeholderMode: boolean = true;

    innerModel: string;

    constructor(
        injector: Injector,
        private appTextService: AppTextService,
        private renderer: Renderer2,
        private contenteditableService: FiContenteditableService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.placeholderMode = !(this.ngModel && this.ngModel.name);

        const name = this.placeholderMode ? this.appTextService.get('task.write_header') : this.ngModel.name;
        const description = this.placeholderMode
            ? this.appTextService.get('task.write_description')
            : this.ngModel.description;
        this.updateInnerModel(name, description, this.placeholderMode);
    }

    setFocusedStatus(isFocused: boolean) {
        this.focused = isFocused;
    }

    onKeydown($event: KeyboardEvent) {
        if (this.placeholderMode) {
            $event.preventDefault();
            this.placeholderMode = false;
            const name = $event.key.length === 1 && /[a-zA-Z0-9-_ ]/.test($event.key) ? $event.key : '';
            this.updateInnerModel(name, '');
            setTimeout(() => this.doPropagateChanges());
        }
    }

    onMousedown($event: MouseEvent) {
        if (this.placeholderMode) {
            $event.preventDefault();
            //$event.stopPropagation();
            this.contenteditableComponent.contenteditableElRef.nativeElement.focus();
        }
    }

    doPropagateChanges() {
        let name = '';
        let description = '';
        let descriptionRowFound = false;

        let shouldUpdateInnerModel = false;
        Array.from(this.contenteditableComponent.contenteditableElRef.nativeElement.childNodes).forEach(
            (el: HTMLElement) => {
                if (el.nodeName === 'DIV') {
                    el.childNodes.forEach((childNode) => {
                        if (childNode.nodeType !== Node.TEXT_NODE) {
                            this.renderer.removeStyle(childNode, 'font-size');
                        }
                    });
                    descriptionRowFound = true;
                }

                if (!descriptionRowFound) {
                    // if we have something like span on 1st row - convert it to plain text
                    if (el.nodeType !== Node.TEXT_NODE) {
                        shouldUpdateInnerModel = true;
                    }
                    name += el.textContent;
                } else {
                    description += `<div>${el.innerHTML}</div>`;
                }
            },
        );

        if (!name) {
            const newNameElement = document.createElement('DIV');
            newNameElement.innerHTML = description;
            name = newNameElement.textContent;
            description = '';
            shouldUpdateInnerModel = true;
        }

        if (shouldUpdateInnerModel) {
            this.updateInnerModel(name, description);
        }

        if (name.length > maxNameLength) {
            let truncatePos = -1;
            for (let i = 0; i < maxNameLength; i++) {
                if (name[i] === ' ') {
                    truncatePos = i;
                }
            }
            if (truncatePos === -1) {
                truncatePos = maxNameLength;
            }
            const nameToTruncate = name.slice(truncatePos);
            name = name.slice(0, truncatePos).trim();
            description = nameToTruncate + description;
            this.updateInnerModel(name, description, undefined, true);
        }
        this.ngModel = {
            name: name,
            description: description,
        };
        super.doPropagateChanges();
    }

    private updateInnerModel(name: string, description, toStart?, isNameTruncated?: boolean) {
        this.innerModel =
            name +
            (isNameTruncated ? '<span class="task-edit-name-ellipsis"></span>' : '') +
            (description ? '<div>' + description + '</div>' : '');
        setTimeout(() =>
            this.contenteditableService.setCursorPosition(
                this.contenteditableComponent.contenteditableElRef.nativeElement,
                toStart,
            ),
        );
    }

    doValidate(): ValidationErrors {
        return {
            required: !this.ngModel || !this.ngModel.name,
        };
    }

    onFilesPasted($event) {
        this.filesPasted.emit($event);
    }
}
